import React, { useState, useEffect, useRef } from 'react';
import { useCarePlanEvents } from '../../utils/reactQueryHooks';
import { getUserValue } from '../../utils/reactQueryHooks';
import './CareEventsModal.css';

const CareEventsModal = ({ 
  isOpen = false, 
  onToggle, 
  maxHeight = "400px",
  hideButton = false 
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragStartHeight, setDragStartHeight] = useState(0);
  const contentRef = useRef(null);
  const userKey = getUserValue();

  // Fetch care plan events
  const { data: carePlanEvents = [], isLoading, error, refetch } = useCarePlanEvents({}, {
    refetchInterval: 30000, // Refresh every 30 seconds
    staleTime: 10000 // Consider data fresh for 10 seconds
  });

  // Debug logging for care events data
  useEffect(() => {
    console.log('[CareEventsModal] Debug Info:', {
      userKey,
      carePlanEventsLength: carePlanEvents?.length || 0,
      carePlanEvents: carePlanEvents,
      isLoading,
      error: error?.message || error,
      isOpen
    });
  }, [carePlanEvents, isLoading, error, userKey, isOpen]);

  // Auto-refresh when modal opens
  useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen, refetch]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      // Validate the date is valid
      if (isNaN(date.getTime())) {
        console.error('Invalid date in CareEventsModal formatTimestamp:', timestamp);
        return 'Invalid Date';
      }
      return date.toLocaleString();
    } catch (error) {
      console.error('Error formatting timestamp in CareEventsModal:', error, timestamp);
      return 'Invalid Date';
    }
  };

  const getExecutionStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'status-completed';
      case 'failed':
        return 'status-failed';
      case 'timeout':
        return 'status-timeout';
      default:
        return 'status-pending';
    }
  };

  const getDeviceResponseClass = (response) => {
    switch (response?.toLowerCase()) {
      case 'success':
        return 'response-success';
      case 'fail':
        return 'response-fail';
      default:
        return 'response-unknown';
    }
  };

  const handleDragStart = (e) => {
    if (e.target.closest('.care-events-table') || e.target.closest('.care-events-header')) {
      return; // Don't start drag if clicking on table or header
    }
    
    setIsDragging(true);
    setDragStartY(e.clientY);
    setDragStartHeight(contentRef.current?.scrollHeight || 0);
    e.preventDefault();
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    
    const deltaY = dragStartY - e.clientY;
    const newHeight = Math.max(200, Math.min(600, dragStartHeight + deltaY));
    
    if (contentRef.current) {
      contentRef.current.style.maxHeight = `${newHeight}px`;
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
      return () => {
        document.removeEventListener('mousemove', handleDragMove);
        document.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging]);

  return (
    <div className="care-events-container">
      {/* Toggle Button - Only show when panel is closed and other modals are not open */}
      {!isOpen && !hideButton && (
        <button 
          className="care-events-toggle-button"
          onClick={onToggle}
          aria-label="Open Care Events viewer"
          title="Open Care Events viewer"
        >
          <i className="ri-calendar-event-line"></i>
          {carePlanEvents.length > 0 && (
            <span className="care-events-badge">{carePlanEvents.length}</span>
          )}
        </button>
      )}

      {/* Console Panel */}
      <div 
        className={`care-events-panel ${isOpen ? 'open' : ''}`}
        style={{ maxHeight: isOpen ? maxHeight : '0px' }}
        onMouseDown={handleDragStart}
      >
        {/* Header */}
        <div className="care-events-header">
          <div className="care-events-header-content">
            <h3 className="care-events-title">
              <i className="ri-calendar-event-line"></i>
              Care Plan Events
            </h3>
            <div className="care-events-header-actions">
              <span className="care-events-count">
                {carePlanEvents.length} events
              </span>
              <button 
                className="care-events-refresh-btn"
                onClick={() => refetch()}
                aria-label="Refresh care events"
                title="Refresh"
              >
                <i className="ri-refresh-line"></i>
              </button>
              <button 
                className="care-events-close-btn"
                onClick={onToggle}
                aria-label="Close Care Events viewer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
          </div>
          <div className="care-events-drag-handle">
            <div className="drag-indicator"></div>
          </div>
        </div>

        {/* Content */}
        <div 
          className="care-events-content"
          ref={contentRef}
          tabIndex={0}
        >
          {error ? (
            <div className="care-events-error">
              <i className="ri-error-warning-line"></i>
              <p>Error loading care events: {error.message}</p>
            </div>
          ) : isLoading ? (
            <div className="care-events-loading">
              <i className="ri-loader-4-line"></i>
              <p>Loading care events...</p>
            </div>
          ) : (
            <div className="care-events-table-container">
              <table className="care-events-table">
                <thead>
                  <tr>
                    <th>⏱️ Timestamp</th>
                    <th>📋 Care Plan</th>
                    <th>🎯 Action Plan</th>
                    <th>🏷️ Device</th>
                    <th>⚙️ Change Type</th>
                    <th>📊 Old Value</th>
                    <th>📈 New Value</th>
                    <th>✅ Status</th>
                    <th>📱 Device Response</th>
                  </tr>
                </thead>
                <tbody>
                  {carePlanEvents.length > 0 ? (
                    carePlanEvents.map((event) => (
                      <tr key={event.event_id || event.id} className="care-events-row">
                        <td className="care-events-time">
                          {formatTimestamp(event.effective_date || event.created_at)}
                        </td>
                        <td className="care-events-care-plan">
                          {event.care_plan_name || 'N/A'}
                        </td>
                        <td className="care-events-action-plan">
                          {event.actuator_description || 'N/A'}
                        </td>
                        <td className="care-events-device">
                          <code>{event.action_mac_address || 'N/A'}</code>
                        </td>
                        <td className="care-events-change-type">
                          <span className={`change-type-badge ${event.change_type?.toLowerCase()}`}>
                            {event.change_type || 'N/A'}
                          </span>
                        </td>
                        <td className="care-events-old-value">
                          {event.old_value || 'N/A'}
                        </td>
                        <td className="care-events-new-value">
                          {event.new_value || 'N/A'}
                        </td>
                        <td className="care-events-status">
                          <span className={`execution-status-badge ${getExecutionStatusClass(event.execution_status)}`}>
                            {event.execution_status || 'N/A'}
                          </span>
                        </td>
                        <td className="care-events-device-response">
                          <span className={`device-response-badge ${getDeviceResponseClass(event.device_response)}`}>
                            {event.device_response || 'N/A'}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr className="care-events-empty-row">
                      <td colSpan="9" className="care-events-empty-cell">
                        <div className="care-events-empty">
                          <i className="ri-calendar-event-line"></i>
                          <p>No care plan events found</p>
                          <small>Care plan execution events will appear here when they occur</small>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CareEventsModal; 